import React, { useCallback, useEffect, useState } from 'react';

import { FormControl, TextInput } from '@primer/react';

import classNames from 'classnames';
import isFunction from 'lodash/isFunction';

export const TextInputWithError = React.forwardRef(({
  className,
  onChange,
  onEntryValidation,
  onValueError,
  onSubmit,
  onBlur,
  placeholder,
  value,
  error,
  errorMessage,
  ariaLabel,
  wrapperStyle,
  block = false,
  ...rest
}, ref) => {
  const [inputError, setError] = useState(null);
  const [isAnimating, setAnimating] = useState(false);
  const [errorMsg, setErrorMsg] = useState(errorMessage);

  const handleChangeEvent = useCallback((event) => {
    setError(null);
    if (isFunction(onChange) && event) {
      onChange(event.target.value);
    }
  }, [onChange])

  const submitValue = useCallback(() => {
    if (isFunction(onSubmit)) {
      onSubmit(value);
    }
  }, [onSubmit, value])

  const checkEntryError = useCallback(() => {
    if (isFunction(onEntryValidation) && !onEntryValidation(value)) {
      const entryError = errorMessage || 'Invalid Entry';

      if (isFunction(onValueError)) {
        onValueError(entryError);
      }
      setError(entryError);
      setAnimating(true);

      return true;
    }

    return false;
  }, [errorMessage, onEntryValidation, onValueError, value])

  const handleKeyPress = useCallback((event) => {
    if (event.key === 'Enter') {
      if (!checkEntryError()) {
        submitValue()
      }
    }
  }, [checkEntryError, submitValue]);

  const handleBlur = useCallback((event) => {
    checkEntryError();
    if (isFunction(onBlur)) {
      onBlur(event);
    }
  }, [checkEntryError, onBlur])

  useEffect(() => {
    if (error) {
      setError(error)
      if (isFunction(onValueError)) {
        setErrorMsg(onValueError(error));
      }
    }
  }, [error, onValueError])

  const validationStatus = inputError ? 'error' : value?.length ? 'success' : '';

  return (
    <div
      className={classNames('w-full', { 'animate-wiggle': isAnimating }, className)}
      onAnimationEnd={() => setAnimating(false)}
      style={wrapperStyle}
    >
      <FormControl>
        <FormControl.Label visuallyHidden>{ariaLabel}</FormControl.Label>
        <TextInput
          {...rest}
          ariaLabel={ariaLabel}
          value={value}
          ref={ref}
          validationStatus={validationStatus}
          placeholder={placeholder}
          onChange={handleChangeEvent}
          onKeyDown={handleKeyPress}
          onBlur={handleBlur}
          block={block}
        />
        {
          (validationStatus === 'error' && errorMsg) ? (
            <FormControl.Validation variant="error">
              {errorMsg}
            </FormControl.Validation>
          ) : null
        }
      </FormControl>
    </div>
  )
});

export default TextInputWithError;
